/**
 * React Query hooks for promo codes.
 *
 * List query + create/update mutations + per-code analytics query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { promoCodesApi } from '../api/promoCodes';
import type {
  PromoCodeListResponse,
  PromoCodeResponse,
  PromoCodeAnalytics,
  PromoCodeCreate,
  PromoCodeUpdate,
  ListPromoCodesParams,
} from '../api/promoCodes';

export const PROMO_CODES_QUERY_KEYS = {
  all: ['promo-codes'] as const,
  list: (params: ListPromoCodesParams) => [...PROMO_CODES_QUERY_KEYS.all, 'list', params] as const,
  analytics: (id: string) => [...PROMO_CODES_QUERY_KEYS.all, 'analytics', id] as const,
};

export function usePromoCodesQuery(
  params: ListPromoCodesParams = {}
): UseQueryResult<PromoCodeListResponse, Error> {
  return useQuery({
    queryKey: PROMO_CODES_QUERY_KEYS.list(params),
    queryFn: () => promoCodesApi.list(params),
    staleTime: 1000 * 30,
  });
}

export function usePromoCodeAnalyticsQuery(
  id: string | null
): UseQueryResult<PromoCodeAnalytics, Error> {
  return useQuery({
    queryKey: PROMO_CODES_QUERY_KEYS.analytics(id ?? ''),
    queryFn: () => promoCodesApi.getAnalytics(id as string),
    enabled: !!id,
    staleTime: 1000 * 30,
  });
}

export function useCreatePromoCodeMutation(): UseMutationResult<
  PromoCodeResponse,
  Error,
  PromoCodeCreate
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PromoCodeCreate) => promoCodesApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROMO_CODES_QUERY_KEYS.all });
    },
  });
}

export function useUpdatePromoCodeMutation(): UseMutationResult<
  PromoCodeResponse,
  Error,
  { id: string; data: PromoCodeUpdate }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PromoCodeUpdate }) =>
      promoCodesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROMO_CODES_QUERY_KEYS.all });
    },
  });
}
